import { Layout } from "@jambushrushti/components/layout/Layout";
import { Seo } from "@jambushrushti/components/shared/Seo";
import { Button } from "@jambushrushti/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@jambushrushti/components/ui/card";
import { Badge } from "@jambushrushti/components/ui/badge";
import { Link } from "react-router-dom";
import {
  BookOpen,
  Search,
  Database,
  Users,
  FileText,
  Globe,
  Layers,
  GitBranch,
  Brain,
  Microscope,
  Library,
  Network,
  BookMarked,
  Languages,
  Scroll,
  ArrowRight,
  CheckCircle,
  Sparkles,
  MessageSquare,
  GraduationCap,
  Building2,
} from "lucide-react";

const manuscriptTools = [
  {
    icon: Microscope,
    title: "Paleographic Analysis",
    description:
      "Advanced tools for analyzing ancient scripts, dating manuscripts, and identifying scribal hands across Jain literature.",
    features: [
      "Script identification",
      "Dating estimation",
      "Comparative analysis",
    ],
  },
  {
    icon: Layers,
    title: "Codicological Studies",
    description:
      "Comprehensive examination of physical manuscript features including materials, binding, and preservation state.",
    features: [
      "Material analysis",
      "Binding techniques",
      "Conservation assessment",
    ],
  },
  {
    icon: Languages,
    title: "Linguistic Analysis",
    description:
      "Multi-language support for Sanskrit, Prakrit, Apabhramsha, and Gujarati text analysis with transliteration tools.",
    features: ["Transliteration", "Grammar parsing", "Etymology tracking"],
  },
  {
    icon: Brain,
    title: "AI-Powered OCR",
    description:
      "State-of-the-art optical character recognition trained on Jain manuscripts for accurate text extraction.",
    features: [
      "Handwriting recognition",
      "Damaged text recovery",
      "Batch processing",
    ],
  },
];

const databases = [
  {
    icon: Library,
    title: "Jain Agam Database",
    description:
      "Complete searchable collection of canonical Jain texts with multiple commentaries and cross-references.",
    records: "45,000+ texts",
    link: "/research/shabdasangraha",
  },
  {
    icon: BookMarked,
    title: "Manuscript Catalog",
    description:
      "Detailed catalog of Jain manuscripts from collections across India and international repositories.",
    records: "12,000+ manuscripts",
    link: "/library",
  },
  {
    icon: FileText,
    title: "Research Archive",
    description:
      "Published research papers, dissertations, and articles on Jain philosophy, history, and literature.",
    records: "8,500+ papers",
    link: "/research/sodhsandarbh",
  },
  {
    icon: Globe,
    title: "Epigraphic Records",
    description:
      "Collection of Jain inscriptions from temples, caves, and archaeological sites with translations.",
    records: "3,200+ inscriptions",
    link: "/research/sodhsanchay",
  },
];

const collaborationOpportunities = [
  {
    icon: Users,
    title: "Research Partnerships",
    description:
      "Collaborate with international scholars and institutions on joint research projects in Jain studies.",
    cta: "Join Network",
    link: "/scholars",
  },
  {
    icon: Building2,
    title: "Institutional Collaboration",
    description:
      "Partner with universities, libraries, and museums for manuscript digitization and preservation projects.",
    cta: "Partner With Us",
    link: "/contact",
  },
  {
    icon: GraduationCap,
    title: "Academic Programs",
    description:
      "Participate in certificate courses, workshops, and seminars on Jain manuscripts and Indology.",
    cta: "Browse Courses",
    link: "/learning/courses",
  },
  {
    icon: MessageSquare,
    title: "Discussion Forum",
    description:
      "Engage with the scholarly community through academic discussions and peer review platforms.",
    cta: "Join Discussion",
    link: "/scholar/feed",
  },
];

const researchDomains = [
  { name: "Jain Philosophy (Darśana)", icon: "🕉️", papers: 2450 },
  { name: "Jain Logic (Nyāya)", icon: "⚖️", papers: 890 },
  { name: "Jain Literature (Sāhitya)", icon: "📜", papers: 1820 },
  { name: "Jain History & Archaeology", icon: "🏛️", papers: 1340 },
  { name: "Jain Art & Iconography", icon: "🎨", papers: 720 },
  { name: "Prakrit & Sanskrit Studies", icon: "📖", papers: 1560 },
  { name: "Comparative Religion", icon: "🔍", papers: 680 },
  { name: "Manuscript Studies", icon: "📚", papers: 1240 },
];

const institutions = [
  "L.D. Institute of Indology, Ahmedabad",
  "Bhandarkar Oriental Research Institute, Pune",
  "Oriental Research Institute, Mysore",
  "School of Oriental and African Studies, London",
  "Centre for Jain Studies, SOAS",
  "University of Chicago Divinity School",
  "Deccan College, Pune",
  "Asiatic Society of Mumbai",
];

export default function IndologyResearch() {
  return (
    <Layout>
      <Seo
        title="Jain Indology Research | Muni Jambuvijayaji Research Center"
        description="Explore advanced manuscript analysis tools, cross-reference databases, and collaboration opportunities in Jain Indology research at Muni Jambuvijayaji Research Center."
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-secondary/50 to-background py-20">
        <div className="absolute inset-0 bg-[url('/pattern-jain.svg')] opacity-5" />
        <div className="container relative mx-auto px-4">
          <div className="mx-auto max-w-4xl text-center">
            <Badge className="mb-4 border-primary/20 bg-primary/10 text-primary">
              <Sparkles className="mr-1 h-3 w-3" />
              Jain Indology Research Hub
            </Badge>
            <h1 className="font-display mb-6 text-4xl font-bold text-foreground md:text-5xl lg:text-6xl">
              Advancing Jain{" "}
              <span className="text-primary">Indological Studies</span>
            </h1>
            <p className="mb-8 text-lg leading-relaxed text-muted-foreground md:text-xl">
              Discover cutting-edge tools for manuscript analysis, access
              comprehensive cross-reference databases, and connect with the
              global community of Jain scholars and researchers.
            </p>
            <div className="flex flex-col justify-center gap-4 sm:flex-row">
              <Button size="lg" asChild>
                <Link to="/research">
                  <Search className="mr-2 h-5 w-5" />
                  Explore Research Tools
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/scholars">
                  <Users className="mr-2 h-5 w-5" />
                  Join Scholar Network
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Manuscript Analysis Tools */}
      <section className="bg-background py-16">
        <div className="container mx-auto px-4">
          <div className="mb-12 text-center">
            <h2 className="font-display mb-4 text-3xl font-bold text-foreground md:text-4xl">
              Manuscript Analysis Tools
            </h2>
            <p className="mx-auto max-w-2xl text-muted-foreground">
              State-of-the-art digital tools designed specifically for
              analyzing, preserving, and studying Jain manuscripts and texts.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {manuscriptTools.map((tool, index) => (
              <Card
                key={index}
                className="group border-border/50 transition-all duration-300 hover:border-primary/30 hover:shadow-lg"
              >
                <CardHeader>
                  <div className="flex items-start gap-4">
                    <div className="rounded-xl bg-primary/10 p-3 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                      <tool.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <CardTitle className="mb-2 text-xl">
                        {tool.title}
                      </CardTitle>
                      <CardDescription className="text-base">
                        {tool.description}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {tool.features.map((feature, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">
                        <CheckCircle className="mr-1 h-3 w-3" />
                        {feature}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-Reference Databases */}
      <section className="bg-secondary/30 py-16">
        <div className="container mx-auto px-4">
          <div className="mb-12 text-center">
            <h2 className="font-display mb-4 text-3xl font-bold text-foreground md:text-4xl">
              Cross-Reference Databases
            </h2>
            <p className="mx-auto max-w-2xl text-muted-foreground">
              Access interconnected databases covering canonical texts,
              manuscripts, research papers, and epigraphic records.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {databases.map((db, index) => (
              <Card
                key={index}
                className="group text-center transition-all duration-300 hover:shadow-lg"
              >
                <CardHeader>
                  <div className="mx-auto mb-4 w-fit rounded-full bg-primary/10 p-4 text-primary transition-transform group-hover:scale-110">
                    <db.icon className="h-8 w-8" />
                  </div>
                  <CardTitle className="text-lg">{db.title}</CardTitle>
                  <Badge className="mx-auto border-gold/30 bg-gold/20 text-gold-foreground">
                    {db.records}
                  </Badge>
                </CardHeader>
                <CardContent>
                  <p className="mb-4 text-sm text-muted-foreground">
                    {db.description}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    asChild
                    className="w-full"
                  >
                    <Link to={db.link}>
                      Access Database
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Research Domains */}
      <section className="bg-background py-16">
        <div className="container mx-auto px-4">
          <div className="mb-12 text-center">
            <h2 className="font-display mb-4 text-3xl font-bold text-foreground md:text-4xl">
              Research Domains
            </h2>
            <p className="mx-auto max-w-2xl text-muted-foreground">
              Explore diverse areas of Jain Indological research with thousands
              of scholarly contributions.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {researchDomains.map((domain, index) => (
              <Card
                key={index}
                className="group cursor-pointer p-4 transition-all hover:border-primary/30 hover:shadow-md"
              >
                <div className="text-center">
                  <span className="mb-2 block text-3xl">{domain.icon}</span>
                  <h3 className="mb-1 text-sm font-medium transition-colors group-hover:text-primary">
                    {domain.name}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {domain.papers.toLocaleString()} papers
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Collaboration Opportunities */}
      <section className="bg-secondary/30 py-16">
        <div className="container mx-auto px-4">
          <div className="mb-12 text-center">
            <h2 className="font-display mb-4 text-3xl font-bold text-foreground md:text-4xl">
              Collaboration Opportunities
            </h2>
            <p className="mx-auto max-w-2xl text-muted-foreground">
              Connect with the global scholarly community and contribute to the
              advancement of Jain Indological research.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {collaborationOpportunities.map((opp, index) => (
              <Card
                key={index}
                className="group transition-all duration-300 hover:shadow-lg"
              >
                <CardHeader className="text-center">
                  <div className="mx-auto mb-4 w-fit rounded-full bg-primary/10 p-4 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                    <opp.icon className="h-6 w-6" />
                  </div>
                  <CardTitle className="text-lg">{opp.title}</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <p className="mb-4 text-sm text-muted-foreground">
                    {opp.description}
                  </p>
                  <Button size="sm" asChild className="w-full">
                    <Link to={opp.link}>
                      {opp.cta}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Institutions */}
      <section className="bg-background py-16">
        <div className="container mx-auto px-4">
          <div className="mb-12 text-center">
            <h2 className="font-display mb-4 text-3xl font-bold text-foreground md:text-4xl">
              Partner Institutions
            </h2>
            <p className="mx-auto max-w-2xl text-muted-foreground">
              Collaborating with leading academic institutions and research
              centers worldwide.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {institutions.map((institution, index) => (
              <div
                key={index}
                className="rounded-lg bg-secondary/50 p-4 text-center transition-colors hover:bg-secondary"
              >
                <Building2 className="mx-auto mb-2 h-6 w-6 text-primary" />
                <p className="text-sm font-medium">{institution}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology Section */}
      <section className="bg-secondary/30 py-16">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-4xl">
            <div className="mb-12 text-center">
              <h2 className="font-display mb-4 text-3xl font-bold text-foreground md:text-4xl">
                Research Methodology
              </h2>
              <p className="text-muted-foreground">
                Our integrated approach to Jain Indological research combines
                traditional scholarship with modern technology.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <Card className="p-6 text-center">
                <div className="mb-2 text-4xl font-bold text-primary">01</div>
                <h3 className="mb-2 font-semibold">Discovery & Acquisition</h3>
                <p className="text-sm text-muted-foreground">
                  Systematic identification and cataloging of manuscripts from
                  repositories worldwide.
                </p>
              </Card>
              <Card className="p-6 text-center">
                <div className="mb-2 text-4xl font-bold text-primary">02</div>
                <h3 className="mb-2 font-semibold">Digitization & Analysis</h3>
                <p className="text-sm text-muted-foreground">
                  High-resolution scanning, OCR processing, and multi-layered
                  textual analysis.
                </p>
              </Card>
              <Card className="p-6 text-center">
                <div className="mb-2 text-4xl font-bold text-primary">03</div>
                <h3 className="mb-2 font-semibold">Publication & Access</h3>
                <p className="text-sm text-muted-foreground">
                  Open access publication and integration into searchable
                  databases for scholars.
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary py-20 text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-display mb-6 text-3xl font-bold md:text-4xl">
            Begin Your Research Journey
          </h2>
          <p className="mx-auto mb-8 max-w-2xl text-lg opacity-90">
            Access world-class resources, connect with leading scholars, and
            contribute to the preservation and advancement of Jain knowledge and
            heritage.
          </p>
          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <Button size="lg" variant="secondary" asChild>
              <Link to="/scholar/login">
                <GraduationCap className="mr-2 h-5 w-5" />
                Register as Scholar
              </Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10"
              asChild
            >
              <Link to="/contact">Contact Research Team</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
